/**
 * Utility functions for formatting strategy codes in the StrategyInfoDrawer
 */

interface CodeItem {
  code: string;
  type: 'CPT' | 'HCPCS' | 'DRUG' | string;
  label?: string;
  value?: string;
}

interface CodeGroup {
  componentId: string;
  codes: CodeItem[];
  conjunction?: string; // "any" or "all" - determines "for any of" vs "for all of"
}

/**
 * Get the color class for a code based on its type
 * Matches the logic from getCodeStyleClasses in utils.tsx
 * Supports: CPT, HCPCS, ICD10P, ICD10D, ICD, DRG, DRUG
 */
export const getCodeColor = (type: string): string => {
  const lowerType = type?.toLowerCase();

  if (lowerType.startsWith('cpt')) return 'text-sky-600';
  if (lowerType.startsWith('hcpcs')) return 'text-magenta-400';
  if (lowerType.startsWith('icd') || lowerType.startsWith('drg'))
    return 'text-purple-600';
  if (lowerType.startsWith('drug')) return 'text-orange-400';

  return 'text-magenta-400'; // default (includes DME, APC, ICDP, etc.)
};

/**
 * Extract codes from a single criteriaInput
 *
 * Supports three formats (in order of precedence):
 * 1. NEW: ui_metadata with code_set format (ui_metadata[0] for type=code_set)
 * 2. NEW: ui_metadata with codes format (ui_metadata array for type=codes)
 * 3. FALLBACK: Plain IDs from DynamicCriteriaGenerator (input.value array)
 */
const extractCodesFromInput = (criteriaInput: any): CodeItem[] => {
  const codes: CodeItem[] = [];

  // Check for ui_metadata with code_set
  if (
    criteriaInput.type === 'code_set' &&
    criteriaInput.ui_metadata &&
    Array.isArray(criteriaInput.ui_metadata) &&
    criteriaInput.ui_metadata[0]
  ) {
    const codeSet = criteriaInput.ui_metadata[0];

    if (codeSet.filters && Array.isArray(codeSet.filters)) {
      codeSet.filters.forEach((filter: any) => {
        if (!filter.values || !Array.isArray(filter.values)) {
          return;
        }

        filter.values.forEach((fullCode: string) => {
          const codeType = filter.type || 'CPT';

          codes.push({
            code: fullCode,
            type: codeType,
          });
        });
      });
    }

    if (codes.length > 0) {
      return codes;
    }
  }

  // Check for ui_metadata with codes
  if (
    criteriaInput.type === 'codes' &&
    criteriaInput.ui_metadata &&
    Array.isArray(criteriaInput.ui_metadata)
  ) {
    criteriaInput.ui_metadata.forEach((codeData: any) => {
      const codeName = codeData.code_name || codeData.id;
      const parts = codeName.split('-');
      const type = parts[0] || 'CPT';

      codes.push({
        code: codeName,
        type,
        label: codeData.code_name,
        value: codeData.id,
      });
    });
    return codes;
  }

  // FALLBACK: Check for plain IDs in value field
  const plainIds = criteriaInput?.value;
  if (plainIds && Array.isArray(plainIds)) {
    plainIds.forEach((code: string) => {
      const parts = code.split('-');
      const type = parts[0] || 'CPT';

      codes.push({
        code,
        type,
      });
    });
    return codes;
  }

  return codes;
};

/**
 * Extract codes from Fresh Faces code_set.filters
 */
const extractCodesFromFreshFaces = (codeSet: any): CodeGroup[] => {
  if (!codeSet?.filters || !Array.isArray(codeSet.filters)) {
    return [];
  }

  const allCodes: CodeItem[] = [];

  // Process each filter in the code_set
  codeSet.filters.forEach((filter: any) => {
    if (!filter.values || !Array.isArray(filter.values)) {
      return;
    }

    // Extract codes from this filter
    filter.values.forEach((fullCode: string) => {
      // Codes come pre-formatted like "CPT-55700"
      // Use the filter.type as the authoritative type (backend confirmed this)
      const codeType = filter.type || 'CPT';

      allCodes.push({
        code: fullCode,
        type: codeType,
      });
    });
  });

  // Return all codes as a single group
  if (allCodes.length > 0) {
    return [
      {
        componentId: 'fresh_faces_codes',
        codes: allCodes,
        conjunction: 'any',
      },
    ];
  }

  return [];
};

/**
 * Extract codes from strategy steps (old Scout List format)
 */
const extractCodesFromSteps = (steps: any[]): CodeGroup[] => {
  const codeGroups: CodeGroup[] = [];

  if (!Array.isArray(steps)) {
    return codeGroups;
  }

  steps.forEach((step, index) => {
    if (step?.params?.codes && Array.isArray(step.params.codes)) {
      const codes: CodeItem[] = step.params.codes.map((code: string) => {
        // Parse the code to get type (CPT, HCPCS, DRUG, etc.)
        const parts = code.split('-');
        const type = parts[0] || 'CPT';

        return {
          code,
          type,
        };
      });

      if (codes.length > 0) {
        codeGroups.push({
          componentId: `step_${index}`,
          codes,
          conjunction: 'any',
        });
      }
    }
  });

  return codeGroups;
};

/**
 * Extract all code groups from criteriaInputs OR from strategyDetails.steps
 * Returns an array of code groups, each with their componentId and codes
 */
export const extractCodeGroups = (
  criteriaInputs: Record<string, any>,
  strategyDetails?: any
): CodeGroup[] => {
  let codeGroups: CodeGroup[] = [];

  // First, try to extract from criteriaInputs (new template-based format)
  const sortedComponentIds = Object.keys(criteriaInputs || {}).sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''), 10);
    const numB = parseInt(b.replace(/\D/g, ''), 10);
    return numA - numB;
  });

  sortedComponentIds.forEach((componentId) => {
    const criteriaInput = criteriaInputs[componentId];

    // Only process inputs that are of type 'codes' or 'code_set'
    if (criteriaInput?.type === 'codes' || criteriaInput?.type === 'code_set') {
      const codes = extractCodesFromInput(criteriaInput);

      if (codes.length > 0) {
        codeGroups.push({
          componentId,
          codes,
          conjunction: 'any',
        });
      }
    }
  });

  // If no codes found in criteriaInputs, try Fresh Faces code_set
  if (codeGroups.length === 0 && strategyDetails?.code_set) {
    codeGroups = extractCodesFromFreshFaces(strategyDetails.code_set);
  }

  // If still no codes, try extracting from steps (Scout List format)
  // Steps can be at strategyDetails.steps OR strategyDetails.query_plan.steps
  if (codeGroups.length === 0) {
    const steps = strategyDetails?.steps || strategyDetails?.query_plan?.steps;

    if (steps) {
      codeGroups = extractCodesFromSteps(steps);
    }
  }

  return codeGroups;
};

/**
 * Format a single code with its proper prefix (CPT-, HCPCS-, DRUG-)
 */
export const formatCode = (code: string, type: string): string => {
  const upperType = type.toUpperCase();

  // If code already has the prefix, return as-is
  if (code.toUpperCase().startsWith(upperType)) {
    return code;
  }

  // Add the prefix
  return `${upperType}-${code}`;
};

/**
 * Segment type for parsed criteria text
 */
export interface CriteriaTextSegment {
  type: 'text' | 'code';
  content: string;
  codeType?: string; // CPT, HCPCS, DRUG, etc.
}

/**
 * Parse plain text criteria and extract code segments
 * Identifies codes in formats like: CPT-97112, HCPCS-12345, DRUG-12345, etc.
 */
export const parseCriteriaText = (
  criteriaText: string
): CriteriaTextSegment[] => {
  if (!criteriaText) return [];

  const segments: CriteriaTextSegment[] = [];

  // Regex to match code patterns: TYPE-NUMBER
  // Supports: CPT, HCPCS, ICD, ICDP, ICD10P, ICD10D, DRG, DRUG, DME, APC
  const codeRegex =
    /\b(CPT|HCPCS|ICD10P|ICD10D|ICDP|ICD|DRG|DRUG|DME|APC)-([A-Z0-9]+)\b/gi;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = codeRegex.exec(criteriaText)) !== null) {
    // Add text before the code
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: criteriaText.substring(lastIndex, match.index),
      });
    }

    // Add the code segment
    segments.push({
      type: 'code',
      content: match[0], // Full match like "CPT-97112"
      codeType: match[1].toUpperCase(), // "CPT"
    });

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < criteriaText.length) {
    segments.push({
      type: 'text',
      content: criteriaText.substring(lastIndex),
    });
  }

  return segments;
};
